import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OAuth2TokenResponse } from '../../shared/models/api.models';
import { OAuth2Service } from './oauth.service';
import { PingFederateOAuthService } from './ping-federate-oauth.service';

export type AuthProviderType = 'entra' | 'pingfederate';

/**
 * Manages auth provider selection, persistence, and provider-specific behaviors
 * (token refresh routing, logout redirection).
 */
@Injectable({
  providedIn: 'root',
})
export class AuthProviderService {
  private static readonly STORAGE_KEY = 'auth_provider';
  private static readonly SESSION_KEY = 'oauth_provider';

  private oauthService = inject(OAuth2Service);
  private pingFederateService = inject(PingFederateOAuthService);

  setProvider(provider: AuthProviderType): void {
    sessionStorage.setItem(AuthProviderService.SESSION_KEY, provider);
    localStorage.setItem(AuthProviderService.STORAGE_KEY, provider);
  }

  getProvider(): AuthProviderType | null {
    return localStorage.getItem(AuthProviderService.STORAGE_KEY) as AuthProviderType | null;
  }

  getSessionProvider(): string | null {
    return sessionStorage.getItem(AuthProviderService.SESSION_KEY);
  }

  clearProvider(): void {
    localStorage.removeItem(AuthProviderService.STORAGE_KEY);
    sessionStorage.removeItem(AuthProviderService.SESSION_KEY);
  }

  /**
   * Returns the appropriate refresh token observable for the current provider.
   */
  refreshAccessToken(refreshToken: string): Observable<OAuth2TokenResponse> {
    return this.getProvider() === 'pingfederate'
      ? this.pingFederateService.refreshAccessToken(refreshToken)
      : this.oauthService.refreshAccessToken(refreshToken);
  }

  /**
   * Returns the logout URL if the provider requires a redirect-based logout,
   * or null if the app should handle logout internally.
   */
  getLogoutUrl(): string | null {
    if (this.getProvider() === 'pingfederate' && environment.pingFederate.logoutUrl) {
      return environment.pingFederate.logoutUrl;
    }
    return null;
  }
}
